import React, { useEffect, useMemo, useState } from 'react';
import { X, Database, Code } from 'lucide-react';

export interface EvidenceLineageRow {
  toolName: string;
  toolArgsHash: string;
  tables: string[];
  timeWindow?: string;
  rowCounts?: Record<string, number>;
  tenantScoped: boolean;
  createdAt: string;
  argsSanitized?: any;
  resultSummary?: any;
  resultStatus?: 'full' | 'truncated';
  resultPreview?: string;
}

function parseRefId(refId: string): { toolName: string; toolArgsHash: string } | null {
  // execution.ts makeLineageRefId => "tool:${toolName}:${argsHash}"
  if (!refId.startsWith('tool:')) return null;
  const parts = refId.split(':');
  if (parts.length < 3) return null;
  return { toolName: parts[1], toolArgsHash: parts.slice(2).join(':') };
}

export function LineageDetailDrawer(props: {
  isOpen: boolean;
  onClose: () => void;
  runId: string;
  lineageRefId: string | null;
  localLineage?: EvidenceLineageRow[];
}): JSX.Element | null {
  const { isOpen, onClose, runId, lineageRefId, localLineage } = props;
  const parsed = useMemo(() => (lineageRefId ? parseRefId(lineageRefId) : null), [lineageRefId]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toolCall, setToolCall] = useState<any>(null);

  const local = useMemo(() => {
    if (!parsed || !Array.isArray(localLineage)) return null;
    return localLineage.find(l => l.toolName === parsed.toolName && l.toolArgsHash === parsed.toolArgsHash) || null;
  }, [parsed, localLineage]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!isOpen || !parsed) return;
      setError(null);
      setToolCall(null);
      setLoading(true);
      try {
        const qs = new URLSearchParams({
          runId,
          toolName: parsed.toolName,
          toolArgsHash: parsed.toolArgsHash
        });
        const resp = await fetch(`/api/agent-core/evidence/tool-call?${qs.toString()}`);
        const json = await resp.json().catch(() => null);
        if (cancelled) return;
        if (!resp.ok) throw new Error(json?.error || `HTTP ${resp.status}`);
        setToolCall(json?.toolCall || null);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load tool call');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, parsed, runId]);

  if (!isOpen || !lineageRefId) return null;

  const display = toolCall || local;

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-[70]" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full max-w-lg bg-white shadow-xl z-[71] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-gray-900">Tool Call</h3>
            <div className="text-xs text-gray-500 font-mono truncate">{lineageRefId}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {loading ? <div className="text-sm text-gray-600">Loading tool call…</div> : null}
          {error ? <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{error}</div> : null}

          {!loading && !error && !display ? (
            <div className="text-sm text-gray-600">No details found.</div>
          ) : null}

          {display ? (
            <>
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-mono text-sm text-gray-900">{display.toolName}</div>
                  <div className="text-xs text-gray-500">{display.timeWindow || ''}</div>
                </div>
                <div className="mt-2 text-xs text-gray-600">
                  tables: {(display.tables || []).join(', ') || '—'} • tenantScoped: <span className="font-mono">{String(display.tenantScoped)}</span>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 text-sm font-medium text-gray-800 flex items-center gap-2">
                  <Code className="w-4 h-4 text-gray-700" />
                  Sanitized Args
                </div>
                <pre className="px-4 py-3 text-xs text-gray-800 overflow-x-auto whitespace-pre-wrap">
                  {JSON.stringify(display.argsSanitized ?? null, null, 2)}
                </pre>
              </div>

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 text-sm font-medium text-gray-800 flex items-center gap-2">
                  <Database className="w-4 h-4 text-blue-600" />
                  Result Summary
                  {display.resultStatus === 'truncated' ? (
                    <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800">truncated</span>
                  ) : null}
                </div>
                <pre className="px-4 py-3 text-xs text-gray-800 overflow-x-auto whitespace-pre-wrap">
                  {display.resultStatus === 'truncated' && display.resultPreview
                    ? display.resultPreview
                    : JSON.stringify(display.resultSummary ?? null, null, 2)}
                </pre>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </>
  );
}


